import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ArrowBack,
  Refresh,
  Badge as RoleManagementIcon,
  ExpandMore as ExpandMoreIcon,
  MarkChatUnreadOutlined as MarkChatUnreadIcon,
} from "@mui/icons-material";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Breadcrumbs,
  Link,
  Collapse,
} from "@mui/material";
import AppointmentIcon from "assets/icons/sidebar/AppointmentIcon";
import BillingIcon from "assets/icons/sidebar/BillingIcon";
import DashboardIcon from "assets/icons/sidebar/DashboardIcon";
import DoctorIcon from "assets/icons/sidebar/DoctorIcon";
import HelpCenterIcon from "assets/icons/sidebar/HelpCenterIcon";
import LearningCertificationIcon from "assets/icons/sidebar/LearningCertificationIcon";
import PatientIcon from "assets/icons/sidebar/PatientIcon";
import PracticeConfigIcon from "assets/icons/sidebar/PracticeConfigIcon";
import PrescriptionIcon from "assets/icons/sidebar/PrescriptionIcon";
import TreatmentPlanIcon from "assets/icons/sidebar/TransactionIcon";
import TransactionIcon from "assets/icons/sidebar/TransactionIcon";
import CelleraLogo from "components/common/CelleraLogo";
import LogoutConfirmationModal from "components/common/LogoutConfirmationModal";
import { EPath } from "constants/path";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router";

import { useCelleraTheme } from "../../theme/useCelleraTheme";

type DashboardLayoutProps = {
  children: React.ReactNode;
  title?: string;
  onNavigate?: (path: string) => void;
  headerActions?: React.ReactNode;
  breadcrumbs?: { label: string; href: string; order: number }[];
  lastBreadcrumb?: string;
  onBack?: () => void;
  onRefresh?: () => void;
  sideButton?: React.ReactNode;
};

interface NavigationItem {
  text: string;
  icon?: React.ReactElement;
  path?: string;
  subItems?: NavigationSubItem[];
}

interface NavigationSubItem {
  text: string;
  path?: string;
  icon?: React.ReactElement;
}

const navigationItems: NavigationItem[] = [
  {
    text: "Dashboard",
    icon: <DashboardIcon />,
    path: EPath.PROVIDER_DASHBOARD,
  },
  {
    text: "Patient",
    icon: <PatientIcon />,
    path: EPath.PATIENT_MANAGEMENT,
  },
  {
    text: "Staff management",
    icon: <DoctorIcon />,
    path: EPath.PRACTICE_MANAGEMENT,
  },
  {
    text: "Practice Profile",
    icon: <DoctorIcon />,
    path: EPath.PRACTICE_PROFILE,
  },
  { text: "Appointment", icon: <AppointmentIcon />, path: EPath.APPOINTMENT },
  {
    text: "Treatment Plans",
    icon: <TreatmentPlanIcon />,
    subItems: [
      {
        text: "Personalized Wellness Protocol",
        path: EPath.TREATMENT_PLAN,
      },
    ],
  },
  {
    text: "Prescription",
    icon: <PrescriptionIcon />,
    path: EPath.PRESCRIPTION_MANAGEMENT,
  },
  {
    text: "Payment Management",
    icon: <BillingIcon />,
    path: EPath.PAYMENT_MANAGEMENT,
  },
  {
    text: "Practice Configuration",
    icon: <PracticeConfigIcon />,
    path: EPath.PRACTICE_CONFIGURATION,
  },
  { text: "Transaction", icon: <TransactionIcon /> },
  {
    text: "Staff Learning & Certification",
    icon: <LearningCertificationIcon />,
    path: EPath.LEARNING_CERTIFICATION,
  },
  {
    text: "Role Management",
    icon: <RoleManagementIcon />,
    path: EPath.ROLE_MANAGEMENT,
  },
  {
    text: "Communications",
    icon: <MarkChatUnreadIcon />,
    path: EPath.COMMUNICATIONS,
  },
];

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  title = "Dashboard",
  onNavigate,
  headerActions,
  breadcrumbs,
  lastBreadcrumb,
  onBack,
  onRefresh,
  sideButton,
}) => {
  const { user, userClaims } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [isNavigating, setIsNavigating] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();
  const { logout } = useAuth();
  const { showSuccess, showError } = useToast();
  const { colors } = useCelleraTheme();

  // State to track expanded menu items
  const [expandedItems, setExpandedItems] = useState<{
    [key: string]: boolean;
  }>({
    "Treatment Plans": false,
  });

  const filteredNavigationItems = React.useMemo(() => {
    if (!userClaims?.isPracticeOwner) {
      const restrictedItems = [
        "Staff management",
        "Practice Profile",
        "Payment Management",
        "Practice Configuration",
        "Role Management",
      ];
      return navigationItems.filter(
        (item) => !restrictedItems.includes(item.text)
      );
    }
    return navigationItems;
  }, [userClaims?.isPracticeOwner]);

  // Determine active item based on current path
  const getActiveItem = () => {
    const currentPath = location.pathname;
    const activeItem = filteredNavigationItems.find(
      (item) => item.path === currentPath
    );
    return activeItem?.text || "Dashboard";
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNavigationClick = (
    item: NavigationItem,
    subItem?: NavigationSubItem
  ) => {
    // If the item has sub-items, toggle the dropdown
    if (item.subItems && !sidebarCollapsed) {
      setExpandedItems((prev) => ({
        ...prev,
        [item.text]: !prev[item.text],
      }));
    }

    // Navigate to the path of the main item or sub-item
    const pathToNavigate = subItem?.path || item.path;
    if (pathToNavigate && !isNavigating) {
      setIsNavigating(true);
      if (onNavigate) {
        onNavigate(pathToNavigate);
      } else {
        navigate(pathToNavigate);
      }
      // Reset navigation state after a short delay
      setTimeout(() => setIsNavigating(false), 300);
    }
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfileClick = () => {
    if (onNavigate) {
      onNavigate(EPath.PROFILE);
    } else {
      navigate(EPath.PROFILE);
    }
    setUserMenuAnchor(null);
  };

  const handleUpdatePasswordClick = () => {
    if (onNavigate) {
      onNavigate(EPath.SETTINGS_RESET_PASSWORD);
    } else {
      navigate(EPath.SETTINGS_RESET_PASSWORD);
    }
    setUserMenuAnchor(null);
  };

  const handleLogoutClick = () => {
    setLogoutModalOpen(true);
    handleUserMenuClose();
  };

  const handleLogoutConfirm = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      showSuccess(
        "Logged out successfully",
        "You have been signed out of your account"
      );
      navigate(EPath.LOGIN);
    } catch (error) {
      console.error("Logout failed:", error);
      showError(
        "Logout failed",
        "There was an error signing you out. Please try again."
      );
    } finally {
      setIsLoggingOut(false);
      setLogoutModalOpen(false);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutModalOpen(false);
  };

  const handleHomeNavigation = () => {
    if (onNavigate) {
      onNavigate(EPath.HOME);
    } else {
      navigate(EPath.HOME);
    }
  };

  const sidebarContent = (
    <Box
      className={`h-full border-r border-gray-300 transition-all duration-300 flex flex-col ${sidebarCollapsed ? "w-16" : "w-80"}`}
      sx={{
        backgroundColor: "#F6F6F6",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* CELLERA Logo Header Section */}
      <Box
        className="flex items-center justify-between p-4 flex-shrink-0"
        sx={{
          height: 72, // Fixed height from Figma: 72px
          backgroundColor: "#F6F6F6", // Surface color from Figma
          borderBottom: "1px solid #E8E8E9", // Bottom border from Figma
        }}
      >
        {!sidebarCollapsed && (
          <Tooltip title="Go to Home Dashboard" arrow>
            <Box
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity duration-200"
              onClick={handleHomeNavigation}
            >
              <CelleraLogo
                width="120"
                height="36"
                fill="#000080"
                className="mr-3"
              />
            </Box>
          </Tooltip>
        )}
        <IconButton
          onClick={handleSidebarToggle}
          className="text-gray-600"
          size="small"
        >
          {sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
        </IconButton>
      </Box>

      {/* Navigation Menu */}
      <Box className="flex-1 px-4 py-2">
        <List className="space-y-1">
          {filteredNavigationItems.map((item) => (
            <React.Fragment key={item.text}>
              <ListItem disablePadding>
                <Tooltip
                  title={sidebarCollapsed ? item.text : ""}
                  placement="right"
                  arrow
                  disableHoverListener={!sidebarCollapsed}
                >
                  <ListItemButton
                    onClick={() => handleNavigationClick(item)}
                    disabled={isNavigating}
                    className={`mb-1 transition-all duration-200 ${
                      getActiveItem() === item.text ? "" : "hover:bg-gray-100"
                    } ${isNavigating ? "opacity-50 cursor-not-allowed" : ""}`}
                    sx={{
                      minHeight: 48,
                      justifyContent: sidebarCollapsed
                        ? "center"
                        : "flex-start",
                      px: sidebarCollapsed ? 1 : 2,
                      backgroundColor:
                        getActiveItem() === item.text && !sidebarCollapsed
                          ? "white"
                          : "transparent",
                      borderRadius: "24px", // Pill-like fully curved borders
                      border:
                        getActiveItem() === item.text && !sidebarCollapsed
                          ? "1px solid #E8E8E9"
                          : "none",
                      "&:hover": {
                        backgroundColor:
                          getActiveItem() === item.text ? "white" : "#f3f4f6",
                        transform: "translateX(2px)",
                      },
                      "&:disabled": {
                        opacity: 0.5,
                        cursor: "not-allowed",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: sidebarCollapsed ? 0 : 2,
                        justifyContent: "center",
                      }}
                    >
                      {React.cloneElement(
                        item?.icon || ((<></>) as React.ReactElement),
                        {
                          stroke:
                            getActiveItem() === item.text
                              ? "#000080"
                              : "#6B7280",
                          fill:
                            getActiveItem() === item.text
                              ? "#000080"
                              : "#6B7280",
                        }
                      )}
                    </ListItemIcon>
                    {!sidebarCollapsed && (
                      <>
                        <ListItemText
                          primary={item.text}
                          sx={{
                            "& .MuiListItemText-primary": {
                              fontSize: "0.875rem",
                              fontWeight:
                                getActiveItem() === item.text ? 500 : 400,
                              color:
                                getActiveItem() === item.text
                                  ? "#000080"
                                  : "#374151",
                            },
                          }}
                        />
                        {item.subItems && (
                          <IconButton
                            size="small"
                            sx={{
                              ml: 1,
                              transform: expandedItems[item.text]
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s",
                            }}
                          >
                            <ExpandMoreIcon />
                          </IconButton>
                        )}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>

              {/* Dropdown Sub-items */}
              {!sidebarCollapsed && item.subItems && (
                <Collapse
                  in={expandedItems[item.text]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItemButton
                        key={subItem.text}
                        sx={{
                          pl: 4,
                          backgroundColor:
                            location.pathname === subItem.path
                              ? "#f0f0f0"
                              : "transparent",
                        }}
                        onClick={() => handleNavigationClick(item, subItem)}
                      >
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            sx: {
                              fontSize: "0.75rem",
                              color:
                                location.pathname === subItem.path
                                  ? "#000080"
                                  : "#6B7280",
                            },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Help Center - Bottom Section */}
      <Box className="px-4 py-2 border-t border-gray-200 flex-shrink-0">
        <ListItem disablePadding>
          <ListItemButton
            className="rounded-lg hover:bg-gray-100"
            sx={{
              minHeight: 48,
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
              px: sidebarCollapsed ? 1 : 2,
              borderRadius: "24px", // Pill-like fully curved borders
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: sidebarCollapsed ? 0 : 2,
                justifyContent: "center",
              }}
            >
              <HelpCenterIcon stroke="#6B7280" fill="#6B7280" />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText
                primary="Help Center"
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    color: "#374151",
                  },
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box className="min-h-screen flex">
      {/* Sidebar */}
      <Box className="flex-shrink-0">{sidebarContent}</Box>

      {/* Main Content */}
      <Box className="flex-1 flex flex-col" sx={{ overflowX: "hidden" }}>
        {/* Header */}
        <AppBar
          position="static"
          className="bg-white shadow-none"
          elevation={0}
          sx={{
            borderBottom: "1px solid #E8E8E9",
            borderRadius: 0,
          }}
        >
          <Toolbar className="py-4 px-4 h-14 justify-between">
            <Box className="flex items-center gap-3">
              <Typography
                variant="h6"
                className="text-xl font-normal leading-7"
                style={{ color: "#1B1B22" }}
              >
                {title}
              </Typography>
              {sideButton && (
                <>
                  <Divider orientation="vertical" variant="middle" flexItem />
                  {sideButton}
                </>
              )}
              {headerActions && (
                <>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      height: 24,
                      alignSelf: "center",
                      borderColor: "#E8E8E9",
                      mx: 1,
                    }}
                  />
                  {headerActions}
                </>
              )}
            </Box>
            <Box className="flex items-center gap-4">
              {/* Notifications */}
              <IconButton className="text-gray-600">
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>

              {/* Search */}
              <IconButton className="text-gray-600">
                <SearchIcon />
              </IconButton>

              {/* Settings */}
              <IconButton className="text-gray-600">
                <SettingsIcon />
              </IconButton>

              {/* User Profile */}
              <Box className="flex items-center gap-2">
                <Box className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <Typography className="text-white text-sm font-medium">
                    {user?.displayName?.charAt(0)?.toUpperCase() ||
                      user?.email?.charAt(0)?.toUpperCase() ||
                      "U"}
                  </Typography>
                </Box>
                <Typography className="text-gray-700 text-sm font-medium">
                  {user?.displayName || user?.email?.split("@")[0] || "User"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleUserMenuOpen}
                  className="text-gray-600"
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Optional Breadcrumbs row under header */}
        {(breadcrumbs && breadcrumbs.length > 0) || lastBreadcrumb ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              paddingTop: 1,
              flexShrink: 0,
              bgcolor: colors.background.paper,
            }}
          >
            <Breadcrumbs separator="/">
              {[...(breadcrumbs ?? [])]
                .sort((a, b) => a.order - b.order)
                .map((item) => (
                  <Link
                    color="inherit"
                    href={item.href}
                    key={item.label}
                    underline="hover"
                  >
                    {item.label}
                  </Link>
                ))}
              {lastBreadcrumb ? (
                <Typography color="text.primary">{lastBreadcrumb}</Typography>
              ) : null}
            </Breadcrumbs>

            {/* Page-level actions (Back/Refresh) */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <IconButton
                title="Go back"
                onClick={onBack}
                size="small"
                sx={{ color: colors.text.secondary }}
              >
                <ArrowBack />
              </IconButton>
              <IconButton
                title="Refresh"
                onClick={onRefresh}
                size="small"
                sx={{ color: colors.text.secondary }}
              >
                <Refresh />
              </IconButton>
            </Box>
          </Box>
        ) : null}

        {/* User Menu Dropdown */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
          <Divider />
          <MenuItem onClick={handleUpdatePasswordClick}>
            Reset Password
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogoutClick}>Logout</MenuItem>
        </Menu>

        {/* Content Area */}
        <Box className="flex-1 p-4 overflow-auto bg-white">{children}</Box>
      </Box>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        open={logoutModalOpen}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        isLoading={isLoggingOut}
      />
    </Box>
  );
};

export default DashboardLayout;
